/**
 * Юнифанс сданного отчёта (04.11, предложение оператора, «да» владельца): во вкладке «Отчёты» рядом
 * с «Медиа игры» — сколько игроков уже в приложении (оператор обещает тайминги, когда зайдут все),
 * ачивки и теги игроков тем же меню, что в форме (UnifansBlock.tsx). Сохраняется сразу, без «Отправить».
 *
 * Исключение из A45, как медиа (ReportMedia.tsx): ставит любой оператор этой игры, напарник тоже.
 * Сервер — game-reports/unifans-card.ts: деньги и статус отчёта не трогает; имена игроков — по правилу
 * ПДн (до конца смены или пока отчёт открыт), после — только счётчик и ачивки.
 * Ревью 04.11: тапы уходят очередью (следующий — после ответа на предыдущий), сервер меняет каждую отметку
 * одним запросом; сбой — экран перечитывается с сервера, причина видна.
 */
import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";
import { api } from "../../api/client";
import type { AchItem } from "./reportFields";
import { UnifansHud, UfFill, UfOf } from "./UnifansBlock";
import { ufCount, withCaptain, withTag, type UfRow } from "./unifansModel";
import { serialQueue } from "./ufEdits";
import m from "./opReport.module.css";
import u from "./unifansBlock.module.css";

interface CardRow extends UfRow { key: string }
interface Card {
  joined: number; expected: number; players_note: string | null;
  achievements: { items: AchItem[]; earned: string[] } | null; players: CardRow[] | null;
}

const errText = (e: unknown, dflt: string) => (e instanceof Error && e.message ? e.message : dflt);

/** Данные блока по брони; грузятся сразу — кнопке нужен счётчик «N из M». */
export function useReportUnifans(bookingId: number) {
  const [card, setCard] = useState<Card | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(() => api<Card>(`/api/game-reports/unifans-card?booking_id=${bookingId}`), [bookingId]);
  useEffect(() => {
    let alive = true;
    load().then((r) => { if (alive) { setCard(r); setError(""); } })
      .catch((e: unknown) => { if (alive) setError(errText(e, "Не удалось загрузить Юнифанс")); });
    return () => { alive = false; };
  }, [load]);
  const reload = useCallback(() => load().then((r) => setCard(r)).catch(() => {}), [load]);
  return { card, setCard, reload, error, count: card ? ufCount(card.joined, card.expected) : null };
}
export type ReportUf = ReturnType<typeof useReportUnifans>;

/** Кнопка «Юнифанс · N из 👤M» рядом с «Медиа игры». */
export function UnifansToggle({ uf, open, onToggle }: { uf: ReportUf; open: boolean; onToggle: () => void }): ReactElement {
  return (
    <button type="button" className={u.ufBtn} aria-expanded={open} onClick={(e) => { e.stopPropagation(); onToggle(); }}
      aria-label={uf.count ? `Юнифанс: в приложении ${uf.count.joined} из ${uf.count.expected}` : "Юнифанс"}>
      Юнифанс{uf.count ? <> · <UfOf c={uf.count} /></> : null}
    </button>
  );
}

export function ReportUnifans({ bookingId, uf }: { bookingId: number; uf: ReportUf }): ReactElement {
  const { card, setCard, reload } = uf;
  const [achOpen, setAchOpen] = useState(false);
  const [help, setHelp] = useState<Set<string>>(new Set());
  const [note, setNote] = useState<{ text: string; err?: boolean } | null>(null);
  const queue = useRef(serialQueue()).current;   // запросы по одному: тап по той же строке ждёт ответа на прошлый
  const seq = useRef(0), failed = useRef(false);   // ответ последнего в очереди — правда обо всех; сбой в середине — перечитать

  /** Оптимистично меняем экран, пишем на сервер; ответ сервера — правда, сбой — перечитываем с сервера и показываем причину. */
  const save = useCallback(<T,>(path: string, body: Record<string, unknown>, draft: (cur: Card) => Card, apply: (r: T, cur: Card) => Card) => {
    const n = ++seq.current;
    setCard((cur) => (cur ? draft(cur) : cur)); if (!failed.current) setNote(null);
    void queue(() => api<T>(path, { method: "POST", body: { booking_id: bookingId, ...body }, retries: 1 })).then((r) => {
      if (n !== seq.current) return;
      if (failed.current) { failed.current = false; void reload(); return; }
      setCard((cur) => (cur ? apply(r, cur) : cur)); setNote({ text: "Сохранено ✓" });
    }, (e: unknown) => {
      failed.current = true; setNote({ text: errText(e, "Не сохранилось — попробуй ещё раз"), err: true });
      if (n === seq.current) { failed.current = false; void reload(); }
    });
  }, [bookingId, queue, reload, setCard]);

  if (!card) return <div className={u.note}>{uf.error || "Загрузка Юнифанса…"}</div>;
  const items = card.achievements?.items ?? [];
  const earned = card.achievements?.earned ?? [];
  const rows = card.players;

  const toggleAch = (id: string) => {
    if (!card.achievements) return;
    const on = !earned.includes(id);
    const flip = (e: string[]) => (on ? (e.includes(id) ? e : [...e, id]) : e.filter((x) => x !== id));
    save<{ earned: string[] }>("/api/game-reports/unifans-card/ach", { id, on },
      (cur) => ({ ...cur, achievements: cur.achievements ? { items: cur.achievements.items, earned: flip(cur.achievements.earned) } : null }),
      (r, cur) => ({ ...cur, achievements: cur.achievements ? { items: cur.achievements.items, earned: r.earned ?? cur.achievements.earned } : null }));
  };
  const writeRow = (i: number, body: Record<string, unknown>, draftRows: (r: CardRow[]) => CardRow[]) => {
    const key = rows?.[i]?.key; if (!key) return;
    save<{ players: CardRow[] }>("/api/game-reports/unifans-card/tag", { key, ...body }, (cur) => ({ ...cur, players: cur.players ? draftRows(cur.players) : cur.players }),
      (r, cur) => ({ ...cur, players: r.players ?? cur.players }));
  };

  return (
    <div className={u.wrap} onClick={(e) => e.stopPropagation()}>
      <UnifansHud count={uf.count} bar>
        <UfFill
          ach={{ items, sel: new Set(earned), help, open: achOpen, onOpen: () => setAchOpen((o) => !o), onSel: toggleAch,
            onHelp: (id) => setHelp((h) => { const x = new Set(h); if (x.has(id)) x.delete(id); else x.add(id); return x; }) }}
          rows={rows}
          onCaptain={(i) => rows && writeRow(i, { captain: true, on: !rows[i]!.captain }, (r) => withCaptain(r, i))}
          onTag={(i, t) => rows && writeRow(i, { tag: t, on: !rows[i]!.tags.includes(t) }, (r) => withTag(r, i, t))}
        />
        {!rows ? <div className={m.ufbar}>{card.players_note || "Имена игроков скрыты"}{items.length ? " · ачивки отметить можно" : ""}</div> : null}
        <div className={`${u.note} ${note?.err ? u.err : ""}`} role="status">{note ? note.text : "Сохраняется сразу"}</div>
      </UnifansHud>
    </div>
  );
}
